import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, merge, switchMap, take, takeUntil, takeWhile, timer } from 'rxjs';
import { DateFormatterService } from '@core/services/helpers/date-formatter.service';
import { DashboardService } from '@services/http/dashboard.service';
import {
  PerformanceHttpService,
  PerformanceStreamCurve,
  PerformanceStreamEvent,
} from '@services/http/performance/performance-http.service';
import { PerformanceMainChartComponent } from '@features/dashboard/control/loan-performance/components/performance-main-chart/performance-main-chart.component';
import { SharedModule } from '@shared/shared.module';
import { Option } from '@interfaces/common/option';
import {
  buildChartDataFromSnapshot,
  buildChartDataFromStream,
} from './performance-chart-data.util';
import { EconomyScenarioSimulationService } from '../economy-scenario-simulation.service';

type BenchmarkType = 'CDI' | 'PRE';
/** Atalhos do filtro de período do gráfico (recorte em memória). */
type PeriodPreset = 'all' | '12m' | '24m' | 'ytd' | 'custom';

/** Mapeamento de benchmark para label e cor da série benchmark no gráfico. */
const BENCHMARK_META: Record<BenchmarkType, { nome: string; cor: string }> = {
  // Benchmark em vermelho e Cota Total em verde (card 3323): a curva da dívida abaixo do benchmark é o
  // resultado esperado, então o verde fica na nossa curva, como no Relatório de Posição.
  CDI: { nome: 'CDI', cor: '#f56565' },
  PRE: { nome: 'PRE', cor: '#ed8936' },
};

/** Opções para o seletor de benchmark. */
const BENCHMARK_OPTIONS: Option[] = [
  { label: 'CDI', value: 'CDI' },
  { label: 'PRE', value: 'PRE' },
];

/**
 * Séries auxiliares do gráfico, controladas pelo dropdown "Séries" (desmarcadas por
 * padrão — por padrão só Cota Total e o benchmark ficam visíveis). Labels devem bater
 * com os datasets montados em `performance-chart-data.util.ts`; cor/tracejado espelham
 * o estilo da série para o swatch do menu.
 */
const AUX_SERIES: { label: string; color: string; dashed?: boolean }[] = [
  { label: 'Juros', color: '#2d3748' },
  { label: 'Câmbio Ponderado', color: '#3182ce', dashed: true },
  { label: 'Derivativos (Curva)', color: '#9f7aea' },
  { label: 'USD Acumulado', color: '#2b6cb0', dashed: true },
  { label: 'EUR Acumulado', color: '#6b46c1', dashed: true },
  { label: 'Cotação USD', color: '#4299e1' },
  { label: 'Cotação EUR', color: '#9f7aea' },
];

/** Configuração base do gráfico (espelha loan-performance.component.ts). */
function buildDefaultChartOptions(): Record<string, unknown> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      // Legenda informativa e enxuta: só as séries VISÍVEIS aparecem (as ocultas são
      // habilitadas pelo dropdown "Séries", não pela legenda) e o clique não alterna
      // visibilidade — evita dessincronizar com o estado do menu.
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          padding: 16,
          font: { size: 13 },
          filter: (item: { hidden?: boolean }) => !item.hidden,
        },
        onClick: () => {},
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: {
            dataset: { label?: string; yAxisID?: string };
            parsed: { y: number };
          }) => {
            const value = context.parsed.y;
            if (context.dataset.yAxisID === 'y1' || context.dataset.yAxisID === 'y2') {
              return `${context.dataset.label}: R$ ${value.toFixed(4)}`;
            }
            return `${context.dataset.label}: ${value.toFixed(2)}%`;
          },
        },
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        ticks: { callback: (value: number) => `${value.toFixed(2)}%` },
        title: { display: true, text: 'Retorno (%)' },
      },
      y1: {
        type: 'linear' as const,
        display: false,
        position: 'right' as const,
        ticks: { callback: (value: number) => `R$ ${value.toFixed(2)}` },
        title: { display: false, text: 'Cotação USD (R$)' },
        grid: { drawOnChartArea: false },
      },
      y2: {
        type: 'linear' as const,
        display: false,
        position: 'right' as const,
        ticks: { callback: (value: number) => `R$ ${value.toFixed(2)}` },
        title: { display: false, text: 'Cotação EUR (R$)' },
        grid: { drawOnChartArea: false },
      },
    },
    elements: { point: { radius: 0, hitRadius: 10, hoverRadius: 4 } },
  };
}

/**
 * Componente standalone OnPush que exibe a curva de performance consolidada
 * da dívida, carregada do snapshot ou calculada via SSE.
 *
 * Permite simular outros benchmarks (CDI/PRE + spread) usando o SSE existente.
 */
@Component({
  selector: 'app-debt-performance-chart',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, PerformanceMainChartComponent],
  templateUrl: './debt-performance-chart.component.html',
  styleUrls: ['./debt-performance-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebtPerformanceChartComponent implements OnChanges, OnDestroy {
  /**
   * Re-consulta o snapshot enquanto o backend responder `status: 'pending'` (snapshot sendo
   * gerado em background) — mesma régua do polling dos cards de economia. Sem isso o gráfico
   * ficava preso no estado vazio até um F5 manual.
   */
  private static readonly PENDING_POLL_INTERVAL_MS = 45_000;
  private static readonly PENDING_POLL_MAX_ATTEMPTS = 20;

  /** Lista de companyIds selecionados no dashboard. */
  @Input() selectedCompanyIds: string[] = [];

  /** Opções do seletor de benchmark. */
  readonly benchmarkOptions: Option[] = BENCHMARK_OPTIONS;

  /** Benchmark selecionado pelo usuário. */
  benchmark: BenchmarkType = 'CDI';

  /** Spread adicional (0–20, inteiro). */
  spread = 0;

  /** Valor editável do spread no dropdown. */
  spreadInput = '0,00';

  /** Controla se o painel dropdown de benchmark está aberto. */
  benchDropdownOpen = false;

  /** Controla se o painel dropdown de séries está aberto. */
  seriesDropdownOpen = false;

  /**
   * Séries auxiliares atualmente visíveis (labels de `AUX_SERIES`). Vazio por padrão —
   * só Cota Total e o benchmark ficam plotados; o usuário liga as demais pelo menu.
   * O estado persiste entre recargas de curva (troca de benchmark/SSE) na mesma sessão.
   */
  private readonly visibleAuxSeries = new Set<string>();

  /** Dados do gráfico para o PerformanceMainChartComponent — sempre um recorte de `fullChartData`. */
  chartData: { labels: string[]; datasets: unknown[] } | null = null;

  /**
   * Curva completa como veio do snapshot/SSE (já com a visibilidade das séries aplicada).
   * O filtro de período recorta labels e os pontos alinhados de cada dataset em memória:
   * nenhuma chamada nova ao backend e nenhum recálculo — a Cota Total continua acumulada
   * desde o início da dívida, só a janela exibida muda (decisão de 15/09/2026).
   */
  private fullChartData: { labels: string[]; datasets: unknown[] } | null = null;

  /** Atalhos do filtro de período (a janela é ancorada no último ponto da curva, não em "hoje"). */
  readonly periodPresets: { label: string; value: PeriodPreset }[] = [
    { label: 'Tudo', value: 'all' },
    { label: 'Últimos 12 meses', value: '12m' },
    { label: 'Últimos 24 meses', value: '24m' },
    { label: 'Ano atual', value: 'ytd' },
    { label: 'Personalizado', value: 'custom' },
  ];

  /** Período selecionado. Persiste entre recargas de curva (troca de benchmark/SSE) na sessão. */
  periodPreset: PeriodPreset = 'all';

  /**
   * Limites do período personalizado. O `app-calendar` entrega `YYYY-MM-DD` (string), então a
   * comparação lexicográfica entre eles é comparação de data — sem `new Date`.
   */
  periodFrom: string | null = null;
  periodTo: string | null = null;

  /** Controla se o painel dropdown de período está aberto. */
  periodDropdownOpen = false;

  /** Opções do gráfico para o PerformanceMainChartComponent. */
  chartOptions: Record<string, unknown> = buildDefaultChartOptions();

  /** Estado de carregamento do snapshot inicial. */
  isLoading = false;

  /** Estado de simulação SSE em andamento. */
  isSimulating = false;

  /** Mensagem de progresso do SSE. */
  streamMessage: string | null = null;

  /** Mensagem de erro (mantém curva anterior). */
  errorMessage: string | null = null;

  /** Rótulo do benchmark do snapshot (ex: "CDI + 2,00%"). */
  snapshotBenchmarkLabel: string | null = null;

  /** Rótulo do benchmark atualmente renderizado no gráfico. */
  activeBenchmarkLabel: string | null = null;

  /** Indica se o gráfico atual é uma simulação (não o snapshot padrão). */
  isShowingSimulation = false;

  /** Geração (UTC, ISO) do snapshot pintado; a prévia do SSE mais velha que ele não o substitui. */
  private snapshotGeneratedAt: string | null = null;

  /** Prévia do SSE na tela: a faixa fica com "Curva de dd/MM HH:mm · atualizando..." até o resultado. */
  private showingPreliminary = false;

  /** Indica se o snapshot carregado incluiu uma linha de benchmark CDI. */
  snapshotHasBenchmarkCurve = false;

  /**
   * Nenhuma empresa selecionada tem benchmark configurado (`status === 'no-benchmark'`).
   * Exibe o estado vazio explicativo "defina o benchmark" em vez do vazio genérico —
   * esse estado só se resolve configurando o benchmark da empresa, não com o tempo.
   */
  isNoBenchmark = false;

  private readonly destroy$ = new Subject<void>();

  /** Subject para cancelar a subscrição SSE anterior quando uma nova é iniciada. */
  private readonly liveCurve$ = new Subject<void>();

  /** Subject para cancelar o snapshot anterior quando a seleção de empresa muda. */
  private readonly snapshotLoad$ = new Subject<void>();

  /**
   * Escopo do snapshot repassado ao SSE: ids das operações elegíveis (`perOperation`) e primeiro
   * ponto da curva. Sem isso o SSE calculava a empresa inteira desde o default do backend
   * (2024-01-01), incluindo operações liquidadas — o gráfico redesenhava com composição e
   * janela diferentes das dos cards (ex.: Zune exibia 2024 por causa da LOAN1034 liquidada)
   * e o cálculo ficava bem mais lento.
   */
  private snapshotScopeLoanIds: string[] = [];
  private snapshotScopeStartDate: string | null = null;

  /** Último contador de reset consumido — evita reagir ao valor inicial do sinal. */
  private lastResetRequestCount: number;

  /** Último pedido de "voltar para hoje" dos cards já consumido (card 3323). */
  private lastPeriodResetCount: number;

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly performanceHttp: PerformanceHttpService,
    private readonly cdr: ChangeDetectorRef,
    private readonly simulationState: EconomyScenarioSimulationService,
    private readonly dateFormatter: DateFormatterService
  ) {
    this.lastResetRequestCount = this.simulationState.resetRequests();
    this.lastPeriodResetCount = this.simulationState.periodResetRequests();

    // Cards pediram "voltar ao benchmark oficial": recarrega snapshot + curva padrão
    // (loadDefault já re-semeia indexador/spread do rótulo do snapshot e limpa a simulação).
    effect(() => {
      const count = this.simulationState.resetRequests();
      if (count !== this.lastResetRequestCount) {
        this.lastResetRequestCount = count;
        this.loadDefault();
      }
    });

    // Cards pediram "voltar para hoje": o gráfico volta a mostrar a curva inteira.
    effect(() => {
      const count = this.simulationState.periodResetRequests();
      if (count !== this.lastPeriodResetCount) {
        this.lastPeriodResetCount = count;
        this.clearPeriod();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCompanyIds']) {
      // Troca de empresa: o período aplicado aos cards era das empresas anteriores.
      this.simulationState.clearPeriod();
      this.loadDefault();
    }
  }

  ngOnDestroy(): void {
    this.snapshotLoad$.next();
    this.snapshotLoad$.complete();
    this.liveCurve$.next();
    this.liveCurve$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Carrega a curva padrão: snapshot (1 empresa) ou SSE (multi-empresa). */
  private loadDefault(): void {
    this.snapshotLoad$.next();
    this.liveCurve$.next();
    // Voltando ao padrão (troca de empresa/reset): os cards devem exibir o benchmark oficial.
    this.simulationState.clear();

    if (!this.selectedCompanyIds?.length) {
      this.chartData = null;
      this.fullChartData = null;
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.chartData = null;
    this.fullChartData = null;
    this.chartOptions = buildDefaultChartOptions();
    this.isShowingSimulation = false;
    this.snapshotHasBenchmarkCurve = false;
    this.activeBenchmarkLabel = null;
    this.isNoBenchmark = false;
    this.snapshotScopeLoanIds = [];
    this.snapshotScopeStartDate = null;
    this.cdr.markForCheck();

    // Polling do 'pending': repete a leitura até o snapshot ficar pronto (takeWhile inclusivo
    // emite também a primeira resposta não-pendente). Troca de empresa/reset cancela via
    // snapshotLoad$; o subscribe abaixo trata cada emissão normalmente.
    timer(0, DebtPerformanceChartComponent.PENDING_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.dashboardService.getEconomyScenario(this.selectedCompanyIds)),
        takeWhile(res => res?.status === 'pending', true),
        take(DebtPerformanceChartComponent.PENDING_POLL_MAX_ATTEMPTS),
        takeUntil(merge(this.destroy$, this.snapshotLoad$))
      )
      .subscribe({
        next: res => {
          this.snapshotBenchmarkLabel = res?.benchmarkLabel ?? null;
          // Semeia indexador e spread a partir do rótulo do snapshot (ex.: "CDI + 2,00%"),
          // para que o botão e o stepper reflitam o benchmark efetivamente plotado.
          this.seedBenchmarkFromLabel(this.snapshotBenchmarkLabel);
          // Escopo para o SSE: mesmas operações elegíveis e mesma janela do snapshot.
          this.snapshotScopeLoanIds = (res?.perOperation ?? []).map(op => op.loanOperationId);
          this.snapshotGeneratedAt = res?.generatedAt ?? null;
          // Em multiempresa a curva vem nula por design (o SSE recalcula a combinada), então o
          // início TEM de vir do campo dedicado — senão o stream sai sem `dataInicio` e o backend
          // usa o default fixo de 01/01/2024, truncando o gráfico (VITALUX + HOLDING começava em
          // 2024 com dívida de 2022, relatado em 19/08/2026).
          this.snapshotScopeStartDate =
            res?.performanceCurveStartDate?.slice(0, 10) ??
            res?.performanceCurve?.total?.curve?.[0]?.data?.slice(0, 10) ??
            null;
          if (res?.requiresLiveCurve) {
            // Multi-empresa: curva via SSE
            this.runLiveCurve(this.benchmark, this.spread);
          } else if (res?.performanceCurve) {
            // Empresa única: mapeia o snapshot diretamente
            const benchmarkCurve = res.benchmarkCurve ?? null;
            this.snapshotHasBenchmarkCurve =
              this.benchmark === 'PRE' ||
              (Array.isArray(benchmarkCurve) && benchmarkCurve.length > 0);
            this.setChartData(
              this.applySeriesVisibility(
                buildChartDataFromSnapshot(
                  res.performanceCurve,
                  this.buildBenchmarkMeta(this.benchmark, this.spread),
                  benchmarkCurve,
                  this.snapshotBenchmarkLabel ?? undefined
                )
              )
            );
            this.syncQuoteAxesVisibility();
            this.activeBenchmarkLabel = this.snapshotBenchmarkLabel;
            this.isShowingSimulation = false;
            this.isLoading = false;
            this.cdr.markForCheck();
            // O snapshot é leve e pinta o gráfico rapidamente, mas não traz dadosDiarios/PTAX.
            // A curva completa entra em seguida para exibir cotações USD/EUR no eixo direito.
            this.runLiveCurve(this.benchmark, this.spread);
          } else {
            // Snapshot pending/sem curva — inclui o caso "no-benchmark" (empresa sem
            // benchmark configurado), que ganha estado vazio explicativo próprio.
            this.isNoBenchmark = res?.status === 'no-benchmark';
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'Não foi possível carregar o cenário de performance.';
          this.cdr.markForCheck();
        },
      });
  }

  /** Aplica o benchmark/spread escolhidos pelo usuário e simula a curva uma única vez. */
  applyBenchmark(): void {
    if (this.isBusy || !this.selectedCompanyIds?.length) return;
    this.normalizeSpreadInput();
    this.errorMessage = null;
    this.activeBenchmarkLabel = null;
    // Publica a simulação para os cards de economia recalcularem contra o mesmo benchmark.
    this.simulationState.apply({ indexer: this.benchmark, spread: this.spread });
    this.runLiveCurve(this.benchmark, this.spread);
  }

  /** Executa o SSE de performance e atualiza o gráfico ao receber o resultado. */
  private runLiveCurve(indexer: BenchmarkType, spread: number): void {
    if (!this.selectedCompanyIds?.length) return;

    // Cancela qualquer stream SSE anterior antes de iniciar um novo
    this.liveCurve$.next();

    this.isSimulating = true;
    this.showingPreliminary = false;
    this.streamMessage = 'Iniciando...';
    this.cdr.markForCheck();

    const meta = this.buildBenchmarkMeta(indexer, spread);

    this.performanceHttp
      .getPerformanceCompletaStream({
        companyId: this.selectedCompanyIds[0],
        companyIds: this.selectedCompanyIds,
        // Mesmo escopo do snapshot: operações elegíveis, janela [primeiro ponto da curva, hoje].
        // Sem isso o backend usa defaults amplos e inclui operações liquidadas (composição e
        // eixo do gráfico divergem dos cards, e o cálculo fica muito mais lento).
        loanIds: this.snapshotScopeLoanIds.length ? this.snapshotScopeLoanIds : undefined,
        dataInicio: this.snapshotScopeStartDate ?? undefined,
        dataFim: this.dateFormatter.formatDateToYYYYMMDD(this.dateFormatter.getTodayDate()),
        benchmarkIndexer: indexer,
        spread,
        taxaAdicionalCdi: spread,
        // O gráfico não lê o detalhe por derivativo: payload ~20x menor e prévia com a última curva.
        includeDerivativeDetails: false,
      })
      .pipe(takeUntil(merge(this.destroy$, this.liveCurve$)))
      .subscribe({
        next: (ev: PerformanceStreamEvent) => {
          if (ev.kind === 'progress') {
            // Com a prévia na tela, a faixa continua dizendo de quando é a curva e que está atualizando.
            if (this.showingPreliminary) return;
            this.streamMessage = ev.message ?? ev.step ?? 'Processando...';
            this.cdr.markForCheck();
            return;
          }
          if (ev.kind === 'error') {
            this.showingPreliminary = false;
            this.isSimulating = false;
            this.isLoading = false;
            this.streamMessage = null;
            this.errorMessage = ev.message || 'Erro ao calcular a curva de performance.';
            // Mantém chartData anterior (não limpa)
            this.cdr.markForCheck();
            return;
          }
          if (ev.kind === 'preliminary') {
            // Última curva calculada: aparece na hora e fica com o selo de atualização até o resultado.
            // Só substitui o snapshot pintado se for mais nova que ele (snapshot refeito depois de um evento
            // não pode voltar à curva de antes durante o recálculo).
            const painted = this.canPaintPreliminary(ev.generatedAt);
            if (painted) this.paintStreamCurve(ev.data, meta, spread);
            this.showingPreliminary = painted;
            // Prévia descartada: o gráfico mostra o snapshot (mais novo), então a faixa não cita a data da prévia.
            const when = painted
              ? this.dateFormatter.formatDateTimeMinutesInstant(ev.generatedAt)
              : '';
            this.streamMessage = !painted
              ? 'Atualizando com os dados de hoje...'
              : when
                ? `Curva de ${when} · atualizando com os dados de hoje...`
                : 'Mostrando a última curva · atualizando com os dados de hoje...';
            this.cdr.markForCheck();
            return;
          }
          if (ev.kind === 'result') {
            this.paintStreamCurve(ev.data, meta, spread);
            this.showingPreliminary = false;
            this.isSimulating = false;
            this.isLoading = false;
            this.streamMessage = null;
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.isSimulating = false;
          this.isLoading = false;
          this.streamMessage = null;
          this.errorMessage = 'Erro ao calcular a curva de performance.';
          this.cdr.markForCheck();
        },
        complete: () => {
          // Stream encerrado sem evento `resultado` (payload descartado no parser):
          // encerra o estado de progresso para não deixar o spinner preso.
          if (this.isSimulating || this.isLoading) {
            this.isSimulating = false;
            this.isLoading = false;
            this.streamMessage = null;
            this.cdr.markForCheck();
          }
        },
      });
  }

  /**
   * A prévia pode ser pintada quando não há curva, quando a curva na tela já veio do SSE ou quando a prévia é
   * mais nova que o snapshot pintado. Datas comparadas em UTC até o segundo (`yyyy-MM-ddTHH:mm:ss`).
   */
  private canPaintPreliminary(generatedAt: string | undefined): boolean {
    if (!this.chartData || this.isShowingSimulation) return true;
    if (!generatedAt || !this.snapshotGeneratedAt) return false;
    return generatedAt.slice(0, 19) > this.snapshotGeneratedAt.slice(0, 19);
  }

  /**
   * Desenha a curva vinda do SSE (prévia ou resultado). Resultado sem curva (ex.: SSE sem dados para o
   * período) NUNCA apaga uma curva já pintada pelo snapshot: só mantém o gráfico.
   */
  private paintStreamCurve(
    data: PerformanceStreamCurve,
    meta: ReturnType<DebtPerformanceChartComponent['buildBenchmarkMeta']>,
    spread: number
  ): void {
    const nextChartOptions = buildDefaultChartOptions();
    const nextBenchmarkLabel =
      spread > 0
        ? `${this.benchmarkButtonLabel} + ${this.formatSpreadValue(spread)}`
        : this.benchmarkButtonLabel;
    const nextChartData = this.applySeriesVisibility(
      buildChartDataFromStream(data, meta, nextChartOptions, nextBenchmarkLabel)
    );
    if (!nextChartData) return;
    this.chartOptions = nextChartOptions;
    this.activeBenchmarkLabel = nextBenchmarkLabel;
    this.setChartData(nextChartData);
    this.isShowingSimulation = true;
    this.syncQuoteAxesVisibility();
  }

  /** Garante que o spread fique no intervalo [0, 20]. */
  clampSpread(): void {
    this.spread = Math.max(0, Math.min(20, this.roundSpread(this.spread)));
    this.spreadInput = this.formatSpreadNumber(this.spread);
  }

  /** Abre/fecha o painel dropdown de benchmark (fecha o de séries). */
  toggleBenchDropdown(): void {
    this.benchDropdownOpen = !this.benchDropdownOpen;
    this.seriesDropdownOpen = false;
    this.periodDropdownOpen = false;
    this.cdr.markForCheck();
  }

  /** Fecha o painel dropdown de benchmark. */
  closeBenchDropdown(): void {
    this.benchDropdownOpen = false;
    this.cdr.markForCheck();
  }

  /** Abre/fecha o painel dropdown de séries (fecha o de benchmark). */
  toggleSeriesDropdown(): void {
    this.seriesDropdownOpen = !this.seriesDropdownOpen;
    this.benchDropdownOpen = false;
    this.periodDropdownOpen = false;
    this.cdr.markForCheck();
  }

  /** Fecha os dois dropdowns (clique fora / Escape). */
  closeDropdowns(): void {
    this.benchDropdownOpen = false;
    this.seriesDropdownOpen = false;
    this.periodDropdownOpen = false;
    this.cdr.markForCheck();
  }

  /**
   * Opções do menu "Séries": apenas as séries auxiliares presentes na curva atual
   * (ex.: EUR só aparece quando a carteira tem operação em EUR), com o swatch na
   * cor/estilo da série.
   */
  get availableSeriesOptions(): { label: string; color: string; dashed?: boolean }[] {
    const datasets = (this.chartData?.datasets ?? []) as { label?: string }[];
    return AUX_SERIES.filter(s => datasets.some(d => d.label === s.label));
  }

  /** Indica se uma série auxiliar está visível no gráfico. */
  isSeriesVisible(label: string): boolean {
    return this.visibleAuxSeries.has(label);
  }

  /** Liga/desliga uma série auxiliar e re-renderiza o gráfico (sem recalcular nada). */
  toggleSeries(label: string): void {
    if (this.visibleAuxSeries.has(label)) {
      this.visibleAuxSeries.delete(label);
    } else {
      this.visibleAuxSeries.add(label);
    }
    this.setChartData(this.applySeriesVisibility(this.fullChartData));
    this.syncQuoteAxesVisibility();
    this.cdr.markForCheck();
  }

  // ===== Filtro de período (recorte em memória, sem backend) =====

  /** Guarda a curva completa e publica o recorte do período escolhido. */
  private setChartData(data: { labels: string[]; datasets: unknown[] } | null): void {
    this.fullChartData = data;
    this.chartData = this.applyPeriodRange(data);
  }

  togglePeriodDropdown(): void {
    this.periodDropdownOpen = !this.periodDropdownOpen;
    if (this.periodDropdownOpen) {
      this.benchDropdownOpen = false;
      this.seriesDropdownOpen = false;
    }
    this.cdr.markForCheck();
  }

  /** Atalho de período: aplica na hora; "Personalizado" só abre os calendários e espera o Aplicar. */
  selectPeriodPreset(value: PeriodPreset): void {
    this.periodPreset = value;
    if (value !== 'custom') {
      this.periodDropdownOpen = false;
      this.refreshPeriodRange();
      return;
    }
    this.cdr.markForCheck();
  }

  /** Aplica o período personalizado (De/Até). Datas invertidas são trocadas em vez de recusadas. */
  applyCustomPeriod(): void {
    if (this.periodFrom && this.periodTo && this.periodFrom > this.periodTo) {
      const swap = this.periodFrom;
      this.periodFrom = this.periodTo;
      this.periodTo = swap;
    }
    this.periodPreset = 'custom';
    this.periodDropdownOpen = false;
    this.refreshPeriodRange();
  }

  /** Volta para a curva inteira. */
  clearPeriod(): void {
    this.periodPreset = 'all';
    this.periodFrom = null;
    this.periodTo = null;
    this.periodDropdownOpen = false;
    this.refreshPeriodRange();
  }

  /**
   * Período resolvido para os cards (card 3323): fim limitado a hoje; atalhos relativos ancorados no
   * último ponto da curva, como o recorte do gráfico. Null quando não há o que aplicar (curva inteira
   * ou fim hoje sem início, que é o mesmo que os cards já mostram).
   */
  private resolveCardsPeriod(): { from: string | null; to: string } | null {
    if (this.periodPreset === 'all' || !this.fullChartData) return null;
    const today = this.dateFormatter.formatDateToYYYYMMDD(this.dateFormatter.getTodayDate());
    const { from, to } = this.resolvePeriodBounds(this.fullChartData.labels);
    const end = to && to < today ? to : today;
    const start = from && from < end ? from : null;
    if (!start && end === today) return null;
    return { from: start, to: end };
  }

  /** Há período diferente de hoje para levar aos cards. */
  get canApplyPeriodToCards(): boolean {
    return !!this.resolveCardsPeriod();
  }

  /** O período do gráfico é o que está aplicado nos cards agora. */
  get isPeriodAppliedToCards(): boolean {
    const applied = this.simulationState.period();
    const current = this.resolveCardsPeriod();
    return !!applied && !!current && applied.from === current.from && applied.to === current.to;
  }

  /**
   * Toggle "Aplicar aos cards": ligado leva o período do gráfico aos cards (economia na data final e no período);
   * desligado devolve os cards para hoje sem mexer no período do gráfico.
   */
  togglePeriodOnCards(applied: boolean): void {
    if (!applied) {
      this.simulationState.clearPeriod();
      return;
    }
    const period = this.resolveCardsPeriod();
    if (!period) return;
    this.simulationState.applyPeriod(period);
  }

  /** Texto do botão "Período" para os atalhos; o personalizado é montado no template com `vaultDate`. */
  get periodButtonValue(): string {
    return this.periodPresets.find(p => p.value === this.periodPreset)?.label ?? 'Tudo';
  }

  /** O período escolhido não deixou nenhum ponto da curva (ex.: janela antes da 1ª dívida). */
  get periodHasNoPoints(): boolean {
    return !!this.fullChartData && !!this.chartData && this.chartData.labels.length === 0;
  }

  private refreshPeriodRange(): void {
    this.chartData = this.applyPeriodRange(this.fullChartData);
    this.syncQuoteAxesVisibility();
    this.cdr.markForCheck();
  }

  /**
   * Recorta `labels` e os pontos de cada dataset pelo mesmo índice. Só datasets cujo `data`
   * está alinhado com `labels` (mesmo tamanho) são recortados; os demais passam intactos.
   * Devolve a mesma referência quando o período cobre a curva inteira (sem re-render à toa).
   */
  private applyPeriodRange(
    data: { labels: string[]; datasets: unknown[] } | null
  ): { labels: string[]; datasets: unknown[] } | null {
    if (!data) return data;
    const { from, to } = this.resolvePeriodBounds(data.labels);
    if (!from && !to) return data;

    const keep: number[] = [];
    data.labels.forEach((label, index) => {
      const key = String(label).slice(0, 10);
      if ((!from || key >= from) && (!to || key <= to)) keep.push(index);
    });
    if (keep.length === data.labels.length) return data;

    return {
      ...data,
      labels: keep.map(i => data.labels[i]),
      // Dataset cujo `data` não está alinhado com `labels` é DESCARTADO no recorte (plotá-lo por
      // índice contra labels recortadas deslocaria a série inteira); hoje todos vêm alinhados.
      datasets: data.datasets.flatMap(ds => {
        const dataset = ds as { data?: unknown[] };
        if (!Array.isArray(dataset.data) || dataset.data.length !== data.labels.length) return [];
        const points = dataset.data;
        return [{ ...dataset, data: keep.map(i => points[i]) }];
      }),
    };
  }

  /**
   * Limites do período como chaves `YYYY-MM-DD` (as labels da curva já vêm nesse formato).
   * Os atalhos relativos são ancorados no ÚLTIMO ponto da curva, não em hoje — uma carteira
   * já liquidada continua mostrando os últimos 12 meses de vida dela.
   */
  private resolvePeriodBounds(labels: string[]): { from: string | null; to: string | null } {
    const last = labels.length ? String(labels[labels.length - 1]).slice(0, 10) : null;
    switch (this.periodPreset) {
      case 'custom':
        return {
          from: this.periodFrom ? this.dateFormatter.formatDateToYYYYMMDD(this.periodFrom) : null,
          to: this.periodTo ? this.dateFormatter.formatDateToYYYYMMDD(this.periodTo) : null,
        };
      case 'ytd': {
        // Ano civil de HOJE (não o ano do último ponto): carteira liquidada em ano anterior mostra o
        // aviso "sem pontos" em vez de fingir que 2024 é o ano atual.
        const currentYear = this.dateFormatter
          .formatDateToYYYYMMDD(this.dateFormatter.getTodayDate())
          .slice(0, 4);
        return { from: `${currentYear}-01-01`, to: null };
      }
      case '12m':
      case '24m':
        return {
          from: last ? this.shiftMonthsBack(last, this.periodPreset === '12m' ? 12 : 24) : null,
          to: null,
        };
      default:
        return { from: null, to: null };
    }
  }

  /**
   * Recua N meses numa chave `YYYY-MM-DD` por aritmética de calendário (sem `new Date`, regra do
   * projeto). O dia é limitado a 28 para a chave nunca cair num dia inexistente do mês-alvo.
   */
  private shiftMonthsBack(key: string, months: number): string {
    let year = Number(key.slice(0, 4));
    let month = Number(key.slice(5, 7)) - months;
    while (month <= 0) {
      month += 12;
      year -= 1;
    }
    const day = Math.min(Number(key.slice(8, 10)) || 1, 28);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  /**
   * Sincroniza a exibição dos eixos direitos (y1 = Cotação USD, y2 = Cotação EUR) com a
   * visibilidade das séries de cotação: eixo só aparece quando a série correspondente está
   * ligada no menu "Séries" — senão a moldura de R$ fica poluindo o gráfico sem linha alguma.
   * Preserva min/max calculados pelo util; gera novas referências para o p-chart re-renderizar.
   */
  private syncQuoteAxesVisibility(): void {
    const options = { ...this.chartOptions };
    const scales = { ...((options['scales'] as Record<string, Record<string, unknown>>) ?? {}) };
    const datasets = (this.chartData?.datasets ?? []) as { label?: string; hidden?: boolean }[];

    const syncAxis = (seriesLabel: string, axisKey: 'y1' | 'y2', axisTitle: string) => {
      if (!scales[axisKey]) return;
      const dataset = datasets.find(d => d.label === seriesLabel);
      const show = !!dataset && !dataset.hidden;
      scales[axisKey] = {
        ...scales[axisKey],
        display: show,
        title: { display: show, text: axisTitle },
      };
    };

    syncAxis('Cotação USD', 'y1', 'Cotação USD (R$)');
    syncAxis('Cotação EUR', 'y2', 'Cotação EUR (R$)');

    options['scales'] = scales;
    this.chartOptions = options;
  }

  /**
   * Normaliza o `hidden` dos datasets auxiliares conforme o estado do menu "Séries"
   * (nova referência de objeto para o OnPush/p-chart re-renderizar). Cota Total e a
   * série de benchmark não passam pelo menu e permanecem sempre visíveis.
   */
  private applySeriesVisibility(
    chartData: { labels: string[]; datasets: unknown[] } | null
  ): { labels: string[]; datasets: unknown[] } | null {
    if (!chartData) return chartData;
    const auxLabels = new Set(AUX_SERIES.map(s => s.label));
    return {
      ...chartData,
      datasets: chartData.datasets.map(ds => {
        const dataset = ds as { label?: string };
        if (!dataset.label || !auxLabels.has(dataset.label)) return ds;
        return { ...dataset, hidden: !this.visibleAuxSeries.has(dataset.label) };
      }),
    };
  }

  /** Seleciona um benchmark no dropdown, sem recalcular até o usuário aplicar. */
  selectBenchmark(value: string): void {
    this.benchmark = value as BenchmarkType;
    this.cdr.markForCheck();
  }

  /** Decrementa o spread no dropdown, sem recalcular até o usuário aplicar. */
  decSpread(): void {
    if (this.spread <= 0 || this.isBusy) return;
    this.spread = Math.max(0, this.spread - 1);
    this.spreadInput = this.formatSpreadNumber(this.spread);
    this.cdr.markForCheck();
  }

  /** Incrementa o spread no dropdown, sem recalcular até o usuário aplicar. */
  incSpread(): void {
    if (this.spread >= 20 || this.isBusy) return;
    this.spread = Math.min(20, this.spread + 1);
    this.spreadInput = this.formatSpreadNumber(this.spread);
    this.cdr.markForCheck();
  }

  onSpreadInputChange(value: string): void {
    this.spreadInput = value;
    const parsed = this.parseSpreadInput(value);
    if (parsed == null) return;

    this.spread = Math.max(0, Math.min(20, parsed));
    this.cdr.markForCheck();
  }

  normalizeSpreadInput(): void {
    this.spreadInput = this.formatSpreadNumber(this.spread);
    this.cdr.markForCheck();
  }

  /** Rótulo base do benchmark, sem spread (ex: "CDI" ou "PRÉ"). */
  get benchmarkButtonLabel(): string {
    return this.benchmark === 'PRE' ? 'PRÉ' : this.benchmark;
  }

  /**
   * Valor exibido no botão de benchmark, já incluindo o spread (ex: "CDI + 2,00%").
   * Prioriza o rótulo do benchmark efetivamente plotado (snapshot ou simulação); na
   * ausência dele, monta o rótulo a partir do indexador e do spread atuais.
   */
  get benchmarkButtonValue(): string {
    if (this.activeBenchmarkLabel) return this.activeBenchmarkLabel;
    const base = this.benchmarkButtonLabel;
    return this.spread > 0 ? `${base} + ${this.formatSpreadValue(this.spread)}` : base;
  }

  /** Rótulo do spread para exibição no stepper (ex: "+0,00%" ou "+2,00%"). */
  get spreadLabel(): string {
    return `+${this.formatSpreadValue(this.spread)}`;
  }

  /** Formata um spread inteiro como percentual no padrão pt-BR (ex.: 2 → "2,00%"). */
  private formatSpreadValue(spread: number): string {
    return `${this.formatSpreadNumber(spread)}%`;
  }

  private formatSpreadNumber(spread: number): string {
    return this.roundSpread(spread).toFixed(2).replace('.', ',');
  }

  private parseSpreadInput(value: string): number | null {
    const normalized = String(value ?? '')
      .replace('%', '')
      .replace(',', '.')
      .trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? this.roundSpread(parsed) : null;
  }

  private roundSpread(spread: number): number {
    return Math.round(spread * 100) / 100;
  }

  private buildBenchmarkMeta(
    indexer: BenchmarkType,
    spread: number
  ): {
    nome: string;
    cor: string;
    tipo: BenchmarkType;
    taxaAnual: number;
  } {
    return {
      ...BENCHMARK_META[indexer],
      tipo: indexer,
      taxaAnual: spread,
    };
  }

  /**
   * Extrai indexador (CDI/PRÉ) e spread de um rótulo como "CDI + 2,00%" e os aplica
   * ao estado do seletor, mantendo botão e stepper consistentes com o benchmark plotado.
   * Falha de parse não altera os valores atuais.
   */
  private seedBenchmarkFromLabel(label: string | null): void {
    if (!label) return;
    const upper = label.toUpperCase();
    if (upper.startsWith('PRE') || upper.startsWith('PRÉ')) {
      this.benchmark = 'PRE';
    } else if (upper.startsWith('CDI')) {
      this.benchmark = 'CDI';
    }
    const match = label.match(/(\d+)(?:[.,]\d+)?\s*%/);
    if (match) {
      const parsedSpread = Number(match[0].replace('%', '').replace(',', '.'));
      this.spread = Math.max(0, Math.min(20, this.roundSpread(parsedSpread)));
      this.spreadInput = this.formatSpreadNumber(this.spread);
    }
  }

  /** Estado geral de carregamento (snapshot ou SSE). */
  get isBusy(): boolean {
    return this.isLoading || this.isSimulating;
  }

  /**
   * Controla a exibição da dica "Selecione um benchmark…".
   * A dica só aparece quando não há linha de benchmark visível:
   * - nem a simulação SSE está ativa (isShowingSimulation = false)
   * - nem o snapshot trouxe uma série CDI (snapshotHasBenchmarkCurve = false)
   */
  get showBenchmarkHint(): boolean {
    return !this.isShowingSimulation && !this.snapshotHasBenchmarkCurve;
  }
}
